import {Observer, observer} from "mobx-react";
import React from "react";

import {bindInput} from "../../../utils/bind-input";
import {SaveButton} from "../../atoms/buttons/buttons";
import {
    ActionsColumn,
    HeaderGrid,
    InputWidthWrapper,
    PageContainer,
} from "../../atoms/layouts/category-layout-containers";
import {FormField} from "../../molecules/inputs/form-field";
import DatabaseRecordRow from "../../molecules/tables/database-record-row";
import {AdminBreadcrumb} from "../../organisms/admin-breadcrumb/admin-breadcrumb";
import {TemplateOptionsDrawer} from "../../organisms/drawers/template-option-drawer";
import {TableLayout} from "../../organisms/table-layout/table-layout";
import {AppLayout} from "../../templates/app-layout/app-layout";
import {DatabaseTemplateRecordCreatePageStore} from "./database-template-record-create-store";

// Props 型定義
type Props = {pageStore: DatabaseTemplateRecordCreatePageStore};

// 1) ヘッダー部：保存ボタンを observer で監視
const RecordCreateHeader = observer(({store}: {store: DatabaseTemplateRecordCreatePageStore}) => (
    <HeaderGrid>
        <InputWidthWrapper width={735}>
            <FormField
                label="データラベル"
                value={store.templateDataLabel}
                errorText={store.templateDataLabelError}
                placeholder="データラベル"
                required
                {...bindInput(store.setTemplateDataLabel)}
            />
        </InputWidthWrapper>
        <ActionsColumn>
            <SaveButton disabled={!store.canCreate} onClick={store.createTemplateItemRecord}>
                保存
            </SaveButton>
        </ActionsColumn>
    </HeaderGrid>
));

// 2) テーブル部：各行を監視（DatabaseRecordRow は内部で observer）
const RecordCreateTable = observer(({store}: {store: DatabaseTemplateRecordCreatePageStore}) => (
    <TableLayout areaLabel="データの追加" columns={store.DEFAULT_COLUMN_DEFS}>
        {store.templateColumnList.map((row) => (
            <DatabaseRecordRow
                key={row.id}
                row={row}
                values={store.values}
                setValue={store.setValue}
                onImageUploaded={store.onImageUploaded}
                openOptions={store.openOptions}
            />
        ))}
    </TableLayout>
));

// 3) ドロワー部：個別 props を Observer でラップ
const RecordCreateDrawer = ({store}: {store: DatabaseTemplateRecordCreatePageStore}) => (
    <Observer>
        {() => (
            <TemplateOptionsDrawer
                isOpen={store.isDrawerOpen}
                onClose={store.closeOptions}
                optionFields={store.optionFields}
                selectedRowLabel={store.selectedRow?.label ?? ""}
                selectedOptions={store.selectedOptions}
                selectedId={store.selectedId}
                setOption={store.setOption}
            />
        )}
    </Observer>
);

// 4) メインコンポーネント：observer を外し、必要な部分だけ再レンダー
const DatabaseTemplateRecordCreatePage: React.FC<Props> = ({pageStore}) => (
    <AppLayout
        breadcrumb={
            <AdminBreadcrumb
                factory={(b) =>
                    b
                        .databaseTemplates()
                        .databaseTemplateDetail(pageStore.templateName, pageStore.templateId)
                        .custom("データの追加")
                        .build()
                }
            />
        }
    >
        <PageContainer>
            <RecordCreateHeader store={pageStore} />
            <RecordCreateTable store={pageStore} />
            <RecordCreateDrawer store={pageStore} />
        </PageContainer>
    </AppLayout>
);

export default DatabaseTemplateRecordCreatePage;
